import React, { useEffect, useState } from "react";
import { Outlet, Link } from "react-router-dom";
import { Leaf, ExternalLink, ShieldCheck, Lock, LogIn } from "lucide-react";
import api from "@/lib/api";

/**
 * Standalone layout for the intake subdomain (intake.choiceayurveda.com).
 *
 * Intentionally minimal — no marketing nav, no footer cross-links.
 * Goal: zero distraction so prospective clients complete the intake.
 */
export const IntakeLayout = () => {
    const [info, setInfo] = useState(null);
    useEffect(() => {
        api.siteInfo().then(setInfo).catch(() => {});
    }, []);

    const mainSiteHost =
        typeof window !== "undefined" && window.location.hostname.startsWith("intake.")
            ? `https://${window.location.hostname.replace(/^intake\./, "")}`
            : "https://choiceayurveda.com";

    return (
        <div className="min-h-screen flex flex-col bg-background bg-grain">
            {/* Minimal header */}
            <header
                data-testid="intake-layout-header"
                className="border-b border-border/70 bg-cream/90 backdrop-blur supports-[backdrop-filter]:bg-cream/75 sticky top-0 z-30"
            >
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16 sm:h-20">
                        {/* Brand */}
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2.5 group"
                            data-testid="intake-layout-logo"
                        >
                            {info?.logo_url ? (
                                <img
                                    src={info.logo_url}
                                    alt="Choice Ayurveda"
                                    className="h-9 sm:h-11 w-auto"
                                />
                            ) : (
                                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-moss text-cream">
                                    <Leaf className="h-4 w-4" strokeWidth={1.75} />
                                </span>
                            )}
                            <div className="leading-tight">
                                <p className="font-display text-lg text-moss">
                                    Choice Ayurveda
                                </p>
                                <p className="text-[0.65rem] uppercase tracking-[0.18em] text-foreground/55 font-accent">
                                    New Client Intake
                                </p>
                            </div>
                        </Link>

                        <div className="flex items-center gap-3 sm:gap-4 text-xs text-foreground/65">
                            <span className="hidden sm:inline-flex items-center gap-1.5">
                                <Lock className="h-3.5 w-3.5 text-sage" />
                                Secure &amp; private
                            </span>
                            <a
                                href={mainSiteHost}
                                target="_blank"
                                rel="noreferrer"
                                className="hidden sm:inline-flex items-center gap-1 text-foreground/70 hover:text-terracotta"
                                data-testid="intake-layout-main-site-link"
                            >
                                Main site
                                <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                            <Link
                                to="/admin/login"
                                data-testid="intake-layout-admin-link"
                                className="inline-flex items-center gap-1 rounded-full bg-sand/80 hover:bg-sand px-3 py-1.5 text-foreground/80 hover:text-moss border border-border/70 transition-colors"
                                title="Practitioner login"
                            >
                                <LogIn className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">Practitioner login</span>
                                <span className="sm:hidden">Login</span>
                            </Link>
                        </div>
                    </div>
                </div>
            </header>

            <main className="flex-1">
                <Outlet />
            </main>

            {/* Minimal footer */}
            <footer className="border-t border-border/70 bg-sand/60">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-foreground/65">
                    <p>
                        &copy; {new Date().getFullYear()} Choice Ayurveda &middot; New
                        Client Intake
                    </p>
                    <div className="flex items-center gap-4">
                        <span className="inline-flex items-center gap-1.5">
                            <ShieldCheck className="h-3.5 w-3.5 text-sage" />
                            Your responses are private and shared only with Riley Ann.
                        </span>
                        <a
                            href={mainSiteHost}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:text-terracotta"
                        >
                            Visit choiceayurveda.com &rarr;
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default IntakeLayout;
